import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Stat } from "../components/UI";
import {
  Tv, Vote, HandCoins, LineChart, ArrowRight, Users, Megaphone,
  Sparkles, ShieldCheck, HeartHandshake, Layers, CheckCircle2, Circle,
} from "lucide-react";
import { FeatureExplorer } from "../components/FeatureExplorer";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));
const fmt = (n) => { if (n >= 1e6) return (n / 1e6).toFixed(1) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return String(n); };

export default function Home() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [overview, setOverview] = useState(null);
  const [fr, setFr] = useState(null);
  const [cc, setCc] = useState(null);
  const [saHealth, setSaHealth] = useState(null);
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    (async () => {
      const [e, o, f, c] = await Promise.all([
        api.get("/political-entities"),
        api.get("/reports/overview"),
        api.get("/fundraising/overview"),
        api.get("/political-os/command-center").catch(() => ({ data: null })),
      ]);
      setEntities(e.data); setOverview(o.data); setFr(f.data); setCc(c.data);
      api.get("/political-os/onboarding/checklist").then(r => setChecklist(r.data)).catch(() => {});
      // Optional admin-only ping; ignore 403
      try { const h = await api.get("/stackadapt/health"); setSaHealth(h.data); } catch { /* not admin */ }
      // If no entity, push to political onboarding
      if ((e.data || []).length === 0) navigate("/onboarding/political");
    })();
  }, [navigate]);

  if (!overview) return <div className="p-8 text-slate-400">Loading…</div>;
  const k = overview.kpis;
  const activeEntity = cc?.entity || entities[0];

  return (
    <div className="p-6 lg:p-8 space-y-8">
      {/* Hero / Command Center */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-cyan-400/[0.08] via-blue-500/[0.05] to-transparent p-8">
        <div className="flex flex-wrap items-start gap-6">
        <div className="max-w-3xl flex-1 min-w-[280px]">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 mb-4" data-testid="command-center-entity-badge">
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-300" />
            <span className="text-xs text-cyan-300 font-medium uppercase tracking-wider">
              {cc?.entity ? `${cc.entity.name} · ${cc.entity.verification_summary?.status}` :
                activeEntity ? `${activeEntity.public_campaign_name || activeEntity.legal_org_name} · ${activeEntity.verification_status}` : "Political mode"}
            </span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]" data-testid="home-hero-title">
            {cc?.entity ? (<>
              <span className="brand-gradient">{cc.entity.name}</span><br />Campaign Command Center.
            </>) : (<>
              The <span className="brand-gradient">Political Advertising & Fundraising</span><br />Operating System.
            </>)}
          </h1>
          <p className="mt-3 text-slate-300 text-lg max-w-2xl">
            {cc?.entity && cc.race ? `${cc.race.name} · ${cc.race.office_label || ""} ${cc.race.state || ""}`.trim()
              : "Political data + CTV + digital advertising + fundraising + AI + compliance + measurement — in one white-labeled cockpit."}
          </p>
        </div>
        {cc?.entity && cc.days_until != null && (
          <div className="text-center px-6 py-4 rounded-2xl border border-cyan-400/20 bg-white/[0.03]" data-testid="election-countdown">
            <div className="font-display text-6xl font-bold bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">{cc.days_until}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Days until election</div>
            <div className="text-[11px] text-slate-500">{cc.election_date}</div>
            <button onClick={() => navigate("/race-intelligence")} data-testid="open-race-intelligence-btn"
              className="mt-3 text-xs text-cyan-300 hover:underline inline-flex items-center gap-1">
              Race Intelligence <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Onboarding checklist widget (hidden once fully complete) */}
      {checklist?.has_entity && !checklist.complete && (
        <Card data-testid="home-onboarding-checklist" className="border-cyan-400/30 bg-gradient-to-r from-cyan-400/[0.05] to-transparent">
          <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
            <div className="shrink-0 mt-1">
              <Sparkles className="w-6 h-6 text-cyan-300" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display text-lg font-bold text-white">Launch checklist</div>
                <div className="text-xs text-slate-400" data-testid="home-checklist-progress">{checklist.done_count}/{checklist.total_steps} done · {Math.round((checklist.progress || 0) * 100)}%</div>
              </div>
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden mb-3">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${Math.round((checklist.progress || 0) * 100)}%` }} />
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(checklist.steps || []).map(s => (
                  <span key={s.key} data-testid={`home-checklist-chip-${s.key}`}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${s.done ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-300" : "border-white/10 text-slate-400"}`}>
                    {s.done ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="shrink-0">
              <Btn onClick={() => navigate("/onboarding/checklist")} data-testid="home-checklist-open-btn">
                Continue setup <ArrowRight className="w-4 h-4" />
              </Btn>
            </div>
          </div>
        </Card>
      )}

      {/* 4 hero actions */}
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-medium mb-3">What would you like to do?</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ActionCard
            testid="action-reach-voters"
            icon={Vote}
            title="Reach Voters"
            body="Create a political advertising campaign across CTV, video, display, and audio."
            cta="Create campaign"
            onClick={() => navigate("/campaigns/new")}
          />
          <ActionCard
            testid="action-raise-money"
            icon={HandCoins}
            title="Raise Money"
            body="Build a compliant fundraising campaign and donation page."
            cta="Start fundraising"
            onClick={() => navigate("/fundraising/campaigns")}
          />
          <ActionCard
            testid="action-do-both"
            icon={HeartHandshake}
            title="Do Both"
            body="Build an integrated advertising + fundraising project sharing one entity."
            cta="Plan with AI"
            onClick={() => navigate("/ai-assistant")}
          />
          <ActionCard
            testid="action-analyze"
            icon={LineChart}
            title="Analyze My Campaign"
            body="Open the War Room to see delivery, sentiment, and fundraising in one view."
            cta="Open War Room"
            onClick={() => navigate("/war-room")}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="kpi-ctv-reach" label="CTV reach" value={fmt(Math.round(k.impressions * 0.4))} change={22} icon={Tv} />
        <Stat testid="kpi-impressions" label="Total impressions" value={fmt(k.impressions)} change={18} icon={Megaphone} />
        <Stat testid="kpi-spend" label="Advertising spend" value={usd(k.spend)} change={-4} icon={Layers} />
        <Stat testid="kpi-raised" label="Funds raised" value={usd(fr?.raised_amount || 0)} change={31} icon={HandCoins} />
      </div>

      {/* Feature Explorer — with per-tile 30-second walkthroughs */}
      <FeatureExplorer />

      {/* CTV hero card */}
      <Card className="relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 mb-3">
              <Tv className="w-3.5 h-3.5" />
              <span className="text-xs font-bold uppercase tracking-wider">Political CTV — Hero product</span>
            </div>
            <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Reach voters with TV-quality creative on every screen.</h3>
            <p className="mt-2 text-slate-300 max-w-2xl">Political data, first-party voter files, district geography, and household reach — activated across the largest CTV inventory pool.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Btn onClick={() => navigate("/campaigns/ctv/new")} data-testid="hero-ctv-btn"><Tv className="w-4 h-4" /> Create CTV campaign</Btn>
              <Btn variant="secondary" onClick={() => navigate("/target-voters")}>Explore voter audiences</Btn>
            </div>
          </div>
          <div className="lg:w-80 grid grid-cols-3 gap-3">
            {[
              { label: "Household reach", value: "3.2M" },
              { label: "Video completion", value: "94%" },
              { label: "Cost / completed view", value: "$0.031" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-white/10 p-3 bg-white/[0.02]">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.label}</div>
                <div className="mt-1 font-display text-xl font-bold brand-gradient">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Political entity + execution strip */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Active political entity</div>
          {activeEntity ? (
            <>
              <div className="mt-2 font-display text-xl font-bold">{activeEntity.public_campaign_name || activeEntity.legal_org_name}</div>
              <div className="text-xs text-slate-400 mt-0.5 capitalize">{activeEntity.entity_type?.replace(/_/g, " ")} · {activeEntity.jurisdiction || "—"}</div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Pill status="Active">{activeEntity.authorization_status?.replace(/_/g, " ")}</Pill>
                <Pill status={activeEntity.verification_status === "verified" ? "Active" : "Pending"}>{activeEntity.verification_status}</Pill>
              </div>
              <Btn variant="ghost" className="mt-4 !px-0" onClick={() => navigate("/organizations")}>Manage entity <ArrowRight className="w-3.5 h-3.5" /></Btn>
            </>
          ) : (
            <>
              <div className="mt-2 text-sm text-slate-400">No political entity yet.</div>
              <Btn className="mt-3" onClick={() => navigate("/onboarding")}>Set up entity</Btn>
            </>
          )}
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Execution engine</div>
          <div className="mt-2 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
              <Layers className="w-4 h-4 text-slate-900" />
            </div>
            <div>
              <div className="font-display text-lg font-bold">Ad Platform</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Primary programmatic partner</div>
            </div>
          </div>
          {saHealth ? (
            <div className="mt-3">
              <Pill status={saHealth.connected ? "Active" : "Rejected"}>{saHealth.connected ? "Live · authenticated" : "Not connected"}</Pill>
              {saHealth.connected && <div className="text-xs text-slate-400 mt-2">{saHealth.advertisers_visible} advertiser(s) visible under this token</div>}
            </div>
          ) : (
            <div className="mt-3 text-xs text-slate-500">Ask your admin about connection status.</div>
          )}
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Fundraising snapshot</div>
          <div className="mt-2 font-display text-3xl font-bold brand-gradient">{usd(fr?.raised_amount || 0)}</div>
          <div className="text-xs text-slate-400 mt-1">of {usd(fr?.goal_amount || 0)} goal · {fr?.total_donors || 0} donors</div>
          <div className="mt-3 h-2 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
              style={{ width: `${Math.min(100, ((fr?.raised_amount || 0) / (fr?.goal_amount || 1)) * 100)}%` }}
            />
          </div>
          <Btn variant="ghost" className="mt-3 !px-0" onClick={() => navigate("/fundraising")}>Open fundraising <ArrowRight className="w-3.5 h-3.5" /></Btn>
        </Card>
      </div>
    </div>
  );
}

const ActionCard = ({ icon: Icon, title, body, cta, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group text-left rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-cyan-400/40 transition-all p-5"
  >
    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400/20 to-blue-500/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 group-hover:scale-105 transition-transform">
      <Icon className="w-5 h-5" />
    </div>
    <div className="mt-4 font-display text-lg font-bold">{title}</div>
    <p className="mt-1 text-sm text-slate-400">{body}</p>
    <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-cyan-300 font-semibold">
      {cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
    </div>
  </button>
);
